import { Component } from '@opten/gin-tonic/helpers/compiler';

interface PartnerSliderOptions {
	/** speed in pixels per second */
	speed?: number;
}

@Component({ className: 'js-partner-slider' })
export class PartnerSliderComponent {
	private track: HTMLElement | null = null;
	private viewport: HTMLElement | null = null;
	private rafId: number | null = null;
	private lastTs: number | null = null;
	private offset = 0; // how far we've scrolled (px)
	private options: Required<PartnerSliderOptions>;
	private viewportWidth = 0;
	private gapX = 0; // measured horizontal gap between items
	private direction = 1; // 1 = leftward (default), -1 = rightward
	private static readonly EPS = 0.5; // small epsilon for subpixel rounding
	// Ensure only one slider row is paused at a time across all instances
	private static currentlyPaused: PartnerSliderComponent | null = null;
	// Track whether this row is currently moving (used for touch interaction on mobile)
	private isMoving = true;
	// Touch gesture tracking to differentiate tap vs swipe
	private touchStartX = 0;
	private touchStartY = 0;
	private touchStartTime = 0;
	private touchMoved = false;
	// Smooth start/stop multipliers (0..1)
	private multiplier = 1;
	private targetMultiplier = 1;
	// How fast multiplier moves toward target (per second). 4 => ~250ms to full stop/start
	private multiplierAccel = 4;

	constructor(private element: HTMLElement, opts: PartnerSliderOptions = {}) {
		// allow overriding via data-speed
		const attrSpeed = parseFloat(element.getAttribute('data-speed') || 'NaN');
		const speed = !Number.isNaN(attrSpeed) ? attrSpeed : opts.speed ?? 30;
		this.options = { speed };
		this.track = element;
		// the "row" container around the track
		this.viewport =
			(this.track.closest(
				'.c-partner-slider__viewport'
			) as HTMLElement | null) || this.track.parentElement;
		// optional per-track direction
		const dirAttr = element.getAttribute('data-direction');
		if (dirAttr !== null) {
			const parsed = parseInt(dirAttr, 10);
			if (!Number.isNaN(parsed) && (parsed === 1 || parsed === -1)) {
				this.direction = parsed;
			} else if (dirAttr.trim().toLowerCase() === 'reverse') {
				this.direction = -1;
			}
		}
		this.setup();
	}

	private setup() {
		if (!this.track) return;
		this.viewportWidth = this.viewport?.clientWidth || window.innerWidth;
		this.ensureBufferContent();
		this.computeGap();
		// Seed reverse rows so there's already an item just outside on the left
		if (this.direction < 0) this.seedReverseStart();
		window.addEventListener('resize', this.onResize, { passive: true });
		// Smooth pause/resume on hovering the ROW (viewport), not individual logos
		if (this.viewport) {
			// Only attach hover listeners on devices that truly support hover
			if (this.canHover()) {
				this.viewport.addEventListener('mouseenter', this.onHoverEnter);
				this.viewport.addEventListener('mouseleave', this.onHoverLeave);
			}
			// Touch interaction: we handle tap vs swipe ourselves; start + move + end
			this.viewport.addEventListener('touchstart', this.onTouchStart, {
				passive: true,
			});
			this.viewport.addEventListener('touchmove', this.onTouchMove, {
				passive: true,
			});
			this.viewport.addEventListener('touchend', this.onTouchEnd);
			this.viewport.addEventListener('touchcancel', this.onTouchCancel);
		}
		this.start();
	}

	/** For reverse direction: ensure one item is pre-pended and offset set, so no pop-in. */
	private seedReverseStart() {
		if (!this.track || this.direction >= 0) return;
		// Only seed if we are at the neutral position (offset ~ 0) to avoid over-adjusting
		if (Math.abs(this.offset) > PartnerSliderComponent.EPS) return;
		const last = this.track.lastElementChild as HTMLElement | null;
		if (!last) return;
		const threshold = last.offsetWidth + this.gapX;
		this.track.insertBefore(last, this.track.firstElementChild);
		this.offset = threshold; // place it just outside the left, ready to scroll in smoothly
	}

	/** Ensure we have enough total width (>= viewport) so no duplicate is visible at once */
	private ensureBufferContent() {
		if (!this.track) return;
		// Remove all clones (keep only the first set)
		const original = Array.from(this.track.children).filter(
			(el: Element) => !(el as HTMLElement).dataset.cloned
		) as HTMLElement[];
		// Remove all previously cloned nodes
		Array.from(this.track.children).forEach((el: Element) => {
			if ((el as HTMLElement).dataset.cloned) this.track!.removeChild(el);
		});
		let totalWidth = this.sumWidths();
		// Clone only until we fill at least the viewport (not 2x)
		while (totalWidth < this.viewportWidth && original.length) {
			for (const node of original) {
				const clone = node.cloneNode(true) as HTMLElement;
				clone.dataset.cloned = '1';
				this.track.appendChild(clone);
			}
			totalWidth = this.sumWidths();
		}
	}
	private pause = () => {
		// kept for backwards-compat: instant stop (but exclusive)
		this.pauseExclusive();
	};

	private resume = () => {
		// kept for backwards-compat: smooth resume (but exclusive)
		this.resumeExclusive();
	};

	private onHoverEnter = () => {
		this.pauseExclusive();
	};

	private onHoverLeave = () => {
		this.resumeExclusive();
	};

	/** touchstart: record initial position/time and reset movement flag */
	private onTouchStart = (e: TouchEvent) => {
		if (e.touches.length > 0) {
			const t = e.touches[0];
			this.touchStartX = t.clientX;
			this.touchStartY = t.clientY;
			this.touchStartTime = performance.now();
			this.touchMoved = false;
		}
	};

	/** touchmove: detect if movement exceeds a small threshold => treat as swipe, not tap */
	private onTouchMove = (e: TouchEvent) => {
		if (this.touchMoved) return;
		if (e.touches.length > 0) {
			const t = e.touches[0];
			const dx = Math.abs(t.clientX - this.touchStartX);
			const dy = Math.abs(t.clientY - this.touchStartY);
			// threshold: 10px movement qualifies as swipe/scroll gesture
			if (dx > 10 || dy > 10) this.touchMoved = true;
		}
	};

	/** touchend: if not moved (tap), toggle pause/resume */
	private onTouchEnd = (_e: TouchEvent) => {
		if (!this.touchMoved) {
			if (this.isMoving) {
				this.pauseExclusive();
			} else {
				this.resumeExclusive();
			}
		}
	};

	/** touchcancel: abandon tap gesture */
	private onTouchCancel = (_e: TouchEvent) => {
		this.touchMoved = true; // prevent toggle on end
	};

	/** Set the desired multiplier and ensure RAF is running to animate toward it. */
	private setTargetMultiplier(target: number) {
		this.targetMultiplier = Math.max(0, Math.min(1, target));
		if (!this.rafId) {
			this.lastTs = null;
			this.rafId = requestAnimationFrame(this.tick);
		}
	}

	private sumWidths(): number {
		if (!this.track) return 0;
		return Array.from(this.track.children).reduce(
			(acc, el) => acc + (el as HTMLElement).offsetWidth,
			0
		);
	}

	private onResize = () => {
		if (!this.track) return;
		this.viewportWidth = this.viewport?.clientWidth || window.innerWidth;
		this.ensureBufferContent();
		this.computeGap();
		if (this.direction < 0) this.seedReverseStart();
	};

	/** Measure horizontal gap between first two items (works with CSS gap or margins). */
	private computeGap() {
		if (!this.track) return;
		const children = this.track.children;
		if (children.length < 2) {
			this.gapX = 0;
			return;
		}
		const first = children[0] as HTMLElement;
		const second = children[1] as HTMLElement;
		// offsetLeft already factors in gap; difference minus first width gives gap
		const raw = second.offsetLeft - (first.offsetLeft + first.offsetWidth);
		this.gapX = raw > 0 ? raw : 0;
	}

	private tick = (ts: number) => {
		if (!this.track) return;
		if (this.lastTs == null) this.lastTs = ts;
		const dt = ts - this.lastTs;
		this.lastTs = ts;
		const delta = (this.options.speed * dt) / 1000; // px per frame at full speed
		// update multiplier toward target
		if (this.multiplier !== this.targetMultiplier) {
			const sign = this.targetMultiplier > this.multiplier ? 1 : -1;
			const change = ((this.multiplierAccel * dt) / 1000) * sign;
			this.multiplier += change;
			if (
				(sign > 0 && this.multiplier > this.targetMultiplier) ||
				(sign < 0 && this.multiplier < this.targetMultiplier)
			) {
				this.multiplier = this.targetMultiplier;
			}
		}
		this.offset += this.direction * delta * this.multiplier;

		if (this.direction >= 0) {
			// normal leftward scroll: recycle first item(s) when fully out of view
			let first = this.track.firstElementChild as HTMLElement | null;
			while (first) {
				const w = first.offsetWidth;
				const threshold = w + this.gapX; // include gap so we shift exact consumed distance
				if (this.offset >= threshold) {
					this.offset -= threshold;
					this.track.appendChild(first); // move to end keeps visual continuity
					first = this.track.firstElementChild as HTMLElement | null;
				} else {
					break;
				}
			}
		} else {
			// reverse/rightward scroll: keep offset non-negative by prepending last item(s)
			// BEFORE rendering so no blank space is ever shown on the left.
			while (this.offset <= PartnerSliderComponent.EPS) {
				const last = this.track.lastElementChild as HTMLElement | null;
				if (!last) break;
				const threshold = last.offsetWidth + this.gapX;
				this.track.insertBefore(last, this.track.firstElementChild);
				this.offset += threshold;
			}
		}

		// apply transform (negative offset to move left)
		this.track.style.transform = `translate3d(${-this.offset}px,0,0)`;

		// If we've reached a full stop and the target is 0, cancel RAF to save CPU.
		if (this.multiplier <= 0 && this.targetMultiplier === 0) {
			if (this.rafId) {
				cancelAnimationFrame(this.rafId);
				this.rafId = null;
			}
			return;
		}
		this.rafId = requestAnimationFrame(this.tick);
	};

	private start() {
		if (this.rafId) cancelAnimationFrame(this.rafId);
		this.lastTs = null;
		this.rafId = requestAnimationFrame(this.tick);
	}

	/** Pause this instance ensuring any other paused instance resumes first. */
	private pauseExclusive() {
		if (
			PartnerSliderComponent.currentlyPaused &&
			PartnerSliderComponent.currentlyPaused !== this
		) {
			PartnerSliderComponent.currentlyPaused.resumeExclusive();
		}
		// Apply local pause only if not already paused
		if (this.isMoving) {
			this.setTargetMultiplier(0);
			this.isMoving = false;
			PartnerSliderComponent.currentlyPaused = this;
		}
	}

	/** Resume this instance if it's the currently paused one. */
	private resumeExclusive() {
		if (PartnerSliderComponent.currentlyPaused === this) {
			this.setTargetMultiplier(1);
			this.isMoving = true;
			PartnerSliderComponent.currentlyPaused = null;
		} else if (!this.isMoving) {
			// If not the globally paused one but locally paused (unlikely), resume locally.
			this.setTargetMultiplier(1);
			this.isMoving = true;
		}
	}

	private canHover(): boolean {
		// Use any-hover so hybrid devices (mouse + touch) still get hover listeners
		return window.matchMedia('(any-hover: hover)').matches;
	}

	public destroy() {
		if (this.rafId) cancelAnimationFrame(this.rafId);
		window.removeEventListener('resize', this.onResize);
		// If this instance is the globally paused one, clear the reference
		if (PartnerSliderComponent.currentlyPaused === this) {
			PartnerSliderComponent.currentlyPaused = null;
		}
		if (this.viewport) {
			this.viewport.removeEventListener('mouseenter', this.onHoverEnter);
			this.viewport.removeEventListener('mouseleave', this.onHoverLeave);
			this.viewport.removeEventListener('touchstart', this.onTouchStart);
			this.viewport.removeEventListener('touchmove', this.onTouchMove);
			this.viewport.removeEventListener('touchend', this.onTouchEnd);
			this.viewport.removeEventListener('touchcancel', this.onTouchCancel);
		}
	}
}

// Note: initialization is handled by loadComponents via the @Component decorator
